import { AsyncSubject, type Observable } from 'rxjs';
import httpService from './httpService';
import type { Trip } from '../data/models/tripleg-monitoring.model';
import type {
  SyncedTripLeg,
  TripLeg,
  TripLegPackage,
} from '../data/models/reservation.model';
import type { AssignTripLeg } from '../data/models/trip-leg-assign.model';
import type { PagedList } from '../data/models/paged-list.model';
import type { TripLegStatusBC } from '../data/models/trip-leg-status.model';
import type { TripLegSearchResponseItem } from '@/data/models/trip-leg-search.model';
import type {
  TripLegRequestV2,
  TripLegSearchRequest,
} from '../data/requests/trip-legs';
import type { InventoryServiceConfiguration } from '../data/models/inventory-service-configuration.model';
import { TripLegDiscussionDecision } from '../data/models/trip-leg-discussion-decision.model';
import {
  TripLegAllocationPropertiesRequest,
  TripLegAllocationPropertiesResponse,
} from '../data/models/trip-leg-allocation.model';
import {
  TripLegConfidenceRequest,
  TripLegConfidenceResponse,
} from '../data/requests/trip-leg-confidence';
import {
  AddressValidationRequest,
  AddressValidationResponse,
} from '../data/requests/address-validation';

export const tripsData: Trip[] = [
  {
    tripID: 123456,
    passenger: 'Emma Taylor',
    pickup: '9:45 AM',
    exception: 'Not receiving trip information',
    lastUpdate: '5 mins',
    timeOnList: '1:00',
  },
  {
    tripID: 123667,
    passenger: 'Stacy Brown',
    pickup: '12:30 AM',
    exception: 'Late',
    lastUpdate: '15 mins',
    timeOnList: '2:03',
  },
  {
    tripID: 345445,
    passenger: 'Emma Brown',
    pickup: '11:30 AM',
    exception: 'Late',
    lastUpdate: '15 mins',
    timeOnList: '2:03',
  },
  {
    tripID: 123667,
    passenger: 'Taylor Brown',
    pickup: '10:30 AM',
    exception: 'Late',
    lastUpdate: '15 mins',
    timeOnList: '2:03',
  },
];

class TriplegService {
  private readonly apiBaseF1Cloud;
  private readonly apiBaseTransport;
  private readonly apiBaseRideshare;
  private readonly apiBaseLocation;

  constructor() {
    const { __environment__ } = window as any;
    this.apiBaseF1Cloud =
      __environment__.REACT_APP_TRIP_LEG_INVENTORY_API_URL.trim();
    this.apiBaseTransport = __environment__.REACT_APP_TRIP_API_URL.trim();
    this.apiBaseRideshare = __environment__.REACT_APP_RIDESHARE_API_URL.trim();
    this.apiBaseLocation = __environment__.REACT_APP_LOCATION_API_URL.trim();
  }

  private getTripLegIdentifier(
    referenceID?: string,
    tripLegID?: number,
  ): string {
    if (!referenceID && !tripLegID) {
      throw new Error(
        'Either referenceID or tripLegID is required when requesting a trip leg',
      );
    }
    return referenceID ? `REF-${referenceID}` : `ID-${tripLegID}`;
  }

  public getAllTrips(): Observable<Trip[]> {
    const subject = new AsyncSubject<Trip[]>();
    subject.next(tripsData);
    subject.complete();
    return subject;
  }

  public getAllTripLegs(): Observable<TripLeg[]> {
    const url = `${this.apiBaseTransport}`;
    return httpService.getJSON(url);
  }

  public getTripLegCounts(): Observable<Record<string, number>> {
    const url = `${this.apiBaseTransport}/counts`;
    return httpService.getJSON(url);
  }

  public getTripLegInventory(
    unallocatedOnly: boolean,
  ): Observable<{ tripLegs: TripLeg[] }> {
    const url = `${this.apiBaseF1Cloud}/getTripLegInventory?allocated=${!unallocatedOnly}`;
    return httpService.getJSON(url);
  }

  public assignTripLegToProvider(
    tripLegInfo: AssignTripLeg,
  ): Observable<TripLeg> {
    const url = `${this.apiBaseF1Cloud}/allocateTripLeg`;
    return httpService.postJSON(url, tripLegInfo);
  }

  public getTripLeg({
    tripLegReferenceID,
    tripLegID,
  }: {
    tripLegReferenceID?: string;
    tripLegID?: number;
  }): Observable<SyncedTripLeg> {
    const identifier = this.getTripLegIdentifier(tripLegReferenceID, tripLegID);
    const url = `${this.apiBaseTransport}/${identifier}`;
    return httpService.getJSON(url);
  }

  public getInternalNotes({
    tripLegID,
    tripLegReferenceID,
  }: {
    tripLegID?: number;
    tripLegReferenceID?: string;
  }) {
    const identifier = this.getTripLegIdentifier(tripLegReferenceID, tripLegID);
    const url = `${this.apiBaseTransport}/${identifier}/internal-notes`;
    return httpService.getJSON(url);
  }

  public createInternalNote({
    tripLegReferenceID,
    tripLegID,
    tripLegInternalNoteComment,
  }: {
    tripLegReferenceID?: string;
    tripLegID?: number;
    tripLegInternalNoteComment: string;
  }) {
    const identifier = this.getTripLegIdentifier(tripLegReferenceID, tripLegID);
    const url = `${this.apiBaseTransport}/${identifier}/internal-notes`;
    return httpService.postJSON(url, {
      note: tripLegInternalNoteComment,
    });
  }

  public validateTripLegAddress(
    confidencePayload: TripLegConfidenceRequest,
    options: {
      /**
       * Whether a low-confidence score is thrown (400) or returned in a 200's
       * `validationErrors`. Callers pass false.
       */
      throwConfidenceError: boolean;
    },
  ): Observable<TripLegConfidenceResponse> {
    const url = `${this.apiBaseLocation}/score/trip-leg?throwConfidenceError=${options.throwConfidenceError}`;
    return httpService.putJSON(url, confidencePayload);
  }

  /**
   * Validates a single address via PUT /validate and returns its location
   * confidence. The payload must include at least one of locationID,
   * googlePlaceID, or addressLine1.
   */
  public validateAddress(
    addressPayload: AddressValidationRequest,
  ): Observable<AddressValidationResponse> {
    const url = `${this.apiBaseLocation}/validate`;
    return httpService.putJSON(url, addressPayload);
  }

  public getTripLegsSearch(
    request: TripLegSearchRequest,
    signal?: AbortSignal,
  ): Observable<PagedList<TripLegSearchResponseItem>> {
    const queryParams = httpService.getQueryParams(request);
    const url = `${this.apiBaseTransport}/search?${queryParams}`;
    return httpService.getJSON(url, signal);
  }

  public cancelRideshareTripLeg({
    tripLegID,
  }: {
    tripLegID: number;
  }): Observable<void> {
    const url = `${this.apiBaseRideshare}/trips/${tripLegID}/cancel`;
    return httpService.postJSON(url, {});
  }

  public getServiceConfiguration(): Observable<InventoryServiceConfiguration> {
    const url = `${this.apiBaseF1Cloud}/getServiceConfiguration`;
    return httpService.getJSON(url);
  }

  public saveServiceConfiguration(
    config: InventoryServiceConfiguration,
  ): Observable<InventoryServiceConfiguration> {
    const url = `${this.apiBaseF1Cloud}/setServiceConfiguration`;
    return httpService.postJSON(url, config);
  }

  public setTripEscalation({
    tripLegReferenceID,
    tripLegID,
  }: {
    tripLegReferenceID?: string;
    tripLegID?: number;
  }): Observable<void> {
    const identifier = this.getTripLegIdentifier(tripLegReferenceID, tripLegID);
    const url = `${this.apiBaseTransport}/${identifier}/escalate`;
    return httpService.postJSON(url, {});
  }

  public clearTripEscalation({
    tripLegReferenceID,
    tripLegID,
  }: {
    tripLegReferenceID?: string;
    tripLegID?: number;
  }): Observable<void> {
    const identifier = this.getTripLegIdentifier(tripLegReferenceID, tripLegID);
    const url = `${this.apiBaseTransport}/${identifier}/clear-escalation`;
    return httpService.postJSON(url, {});
  }

  public setTripLegExceptionAssignment({
    tripLegReferenceID,
    tripLegID,
    userAccountID,
  }: {
    tripLegReferenceID?: string;
    tripLegID?: number;
    userAccountID: number;
  }): Observable<void> {
    const identifier = this.getTripLegIdentifier(tripLegReferenceID, tripLegID);
    const url = `${this.apiBaseTransport}/${identifier}/exception/assign`;
    return httpService.postJSON(url, { userAccountID });
  }

  public setTripLegException({
    tripLegReferenceID,
    tripLegID,
  }: {
    tripLegReferenceID?: string;
    tripLegID?: number;
  }): Observable<void> {
    const identifier = this.getTripLegIdentifier(tripLegReferenceID, tripLegID);
    const url = `${this.apiBaseTransport}/${identifier}/exception`;
    return httpService.postJSON(url, {});
  }

  public clearTripLegException({
    tripLegReferenceID,
    tripLegID,
  }: {
    tripLegReferenceID?: string;
    tripLegID?: number;
  }): Observable<void> {
    const identifier = this.getTripLegIdentifier(tripLegReferenceID, tripLegID);
    const url = `${this.apiBaseTransport}/${identifier}/clear-exception`;
    return httpService.postJSON(url, {});
  }

  public getProvidersMatchingTripLeg({
    tripLeg,
    signal,
  }: {
    tripLeg: TripLeg;
    signal?: AbortSignal;
  }): Observable<{ transportationProviderIDs: number[] }> {
    const url = `${this.apiBaseF1Cloud}/getProvidersMatchingTripLegProperties`;
    return httpService.postJSON(url, tripLeg, false, signal);
  }

  public saveProviderCallOutcome({
    callOutcomeID,
    callOutcomeRejectReasonID,
    rejectedReason,
    serviceAssurance,
    status,
    tripLegReferenceID,
    tripLegID,
    transportationProviderID,
  }: {
    callOutcomeID: number;
    callOutcomeRejectReasonID?: number;
    rejectedReason?: string;
    serviceAssurance?: string;
    status?: TripLegStatusBC;
    tripLegReferenceID?: string;
    tripLegID?: number;
    transportationProviderID: number;
  }): Observable<void> {
    const identifier = this.getTripLegIdentifier(tripLegReferenceID, tripLegID);
    const url = `${this.apiBaseTransport}/${identifier}/find-provider-call-outcome?transportationProviderID=${transportationProviderID}`;
    return httpService.postJSON(url, {
      callOutcomeID,
      callOutcomeRejectReasonID,
      rejectedReason,
      serviceAssurance,
      status,
      // transportationProviderID: transportationProviderID,
    });
  }

  public manuallyAssignTripLegToProvider({
    tripLegReferenceID,
    status,
    transportationProviderID,
    clientTransportationServiceUUID,
    providerTransportationServiceUUID,
    passengerUUID,
    coverageAreaID,
    tripLegRejectedReasonBC,
  }: AssignTripLeg): Observable<void> {
    const url = `${this.apiBaseF1Cloud}/manuallyAssignTripLegToProvider`;
    return httpService.postJSON(url, {
      passengerUUID,
      tripLegReferenceID,
      status,
      transportationProviderID,
      clientTransportationServiceUUID,
      providerTransportationServiceUUID,
      coverageAreaID,
      tripLegRejectedReasonBC,
    });
  }

  public getValidTripLegAllocationProperties(
    request: TripLegAllocationPropertiesRequest,
  ): Observable<TripLegAllocationPropertiesResponse> {
    const url = `${this.apiBaseF1Cloud}/getValidTripLegAllocationProperties`;
    const queryParams = Object.entries(request)
      .filter(([_, value]) => value !== undefined && value !== null)
      .map(([key, value]) => {
        return `${encodeURIComponent(key)}=${encodeURIComponent(value.toString())}`;
      })
      .join('&');
    return httpService.getJSON(`${url}?${queryParams}`);
  }

  public getDiscussion({
    tripLegReferenceID,
    tripLegID,
  }: {
    tripLegReferenceID?: string;
    tripLegID?: number;
  }): Observable<TripLegRequestV2> {
    const identifier = this.getTripLegIdentifier(tripLegReferenceID, tripLegID);
    const url = `${this.apiBaseTransport}/${identifier}/under-discussion`;
    return httpService.getJSON(url);
  }

  public assignDriver({
    tripLegReferenceID,
    tripLegID,
    driverID,
    sendSMS = true,
  }: {
    tripLegReferenceID?: string;
    tripLegID?: number;
    driverID: number;
    sendSMS?: boolean;
  }): Observable<void> {
    const identifier = this.getTripLegIdentifier(tripLegReferenceID, tripLegID);
    const url = `${this.apiBaseTransport}/${identifier}/assign-driver`;
    return httpService.postJSON(url, { driverID, updateOnly: !sendSMS });
  }

  public clearUnderDiscussion({
    tripLegReferenceID,
    tripLegID,
    decision,
  }: {
    tripLegReferenceID?: string;
    tripLegID?: number;
    decision: TripLegDiscussionDecision;
  }): Observable<void> {
    const identifier = this.getTripLegIdentifier(tripLegReferenceID, tripLegID);
    const url = `${this.apiBaseTransport}/${identifier}/clear-under-discussion`;
    return httpService.postJSON(url, decision);
  }

  public setException({
    tripLegReferenceID,
    tripLegID,
    tripLegExceptionID,
  }: {
    tripLegReferenceID?: string;
    tripLegID?: number;
    tripLegExceptionID: number;
  }): Observable<TripLegPackage> {
    const identifier = this.getTripLegIdentifier(tripLegReferenceID, tripLegID);
    const url = `${this.apiBaseTransport}/${identifier}/exception`;
    return httpService.postJSON(url, { tripLegID, tripLegExceptionID });
  }
}

const triplegService = new TriplegService();

export default triplegService;
